import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { BadgeCheck, XCircle, Loader } from 'lucide-react';
import { useVerifyEmail } from '@/hooks/useApi';
import { useQueryClient } from 'react-query';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const { mutate: verify } = useVerifyEmail();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');

  useEffect(() => {
    if (!token) { setStatus('error'); return; }
    verify(token, {
      onSuccess: () => {
        queryClient.invalidateQueries(['currentUser']);
        setStatus('success');
      },
      onError: () => setStatus('error'),
    });
  }, [token]);

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-900 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-sm border border-gray-200 dark:border-zinc-800 p-8 max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <Loader className="h-12 w-12 text-brand-500 animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 dark:text-zinc-100">Verifying your email…</h1>
          </>
        )}
        {status === 'success' && (
          <>
            <BadgeCheck className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 dark:text-zinc-100 mb-2">Email verified!</h1>
            <p className="text-gray-500 dark:text-zinc-500 mb-6">Your account is now verified. A checkmark badge will appear on your profile.</p>
            <Link to="/dashboard" className="btn-primary px-6 py-2">Go to dashboard</Link>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 dark:text-zinc-100 mb-2">Link invalid or expired</h1>
            <p className="text-gray-500 dark:text-zinc-500 mb-6">This verification link has expired or already been used. Request a new one from Settings.</p>
            <Link to="/settings" className="btn-primary px-6 py-2">Go to Settings</Link>
          </>
        )}
      </div>
    </div>
  );
}
